import FolioRadioGroup from '@/components/FolioRadioGroup';
import { useLazyInsightEndpointQuery } from '@/features/properties/propertiesApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	Autocomplete,
	CircularProgress,
	InputAdornment,
	Skeleton,
	TextField,
	useTheme,
} from '@mui/material';
import Fuse from 'fuse.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const FUSE_OPTIONS = {
	keys: ['Name'],
	threshold: 0.2,
};

const InsightSelect = ({
	endpoint,
	value,
	disabled,
	onChange,
	minimal = false,
	loading = false,
	onBlur = (e) => {},
	error = false,
	canSearch = false,
	clearable = false,
	isOptionDisabled = (option) => false,
	variant = 'autocomplete',
	autofillIfNull = false,
	...props
}) => {
	const theme = useTheme();

	const minimalInputTheme = theme.components.MuiInputBase.styleOverrides.minimal;

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const [page, setPage] = useState(1);
	const [pageCount, setPageCount] = useState();
	const [pages, setPages] = useState({});

	const [getInsightData, insightQueryState] = useLazyInsightEndpointQuery();

	useEffect(() => {
		const getPage = async () => {
			const newData = await getInsightData({ endpoint, PageNumber: page }).unwrap();
			if (newData) {
				setPages((prev) => ({ ...prev, [newData.PageNumber]: newData.Results ?? [] }));
				setPageCount(newData.PageCount);
				setPage(newData.PageNumber + 1);
			}
		};
		if (userDetails && (pageCount == null || page <= pageCount)) {
			getPage();
		}
	}, [userDetails, page, pageCount]);

	const options = useMemo(() => {
		const d = Object.values(pages).flat();
		return d.sort((a, b) => a?.Name ?? ''.localeCompare(b?.Name ?? ''));
	}, [pages]);

	const searchFuse = useMemo(() => {
		const fuseIndex = Fuse.createIndex(FUSE_OPTIONS.keys, options);
		return new Fuse(options, FUSE_OPTIONS, fuseIndex);
	}, [options]);

	const allowSearch = useMemo(() => canSearch || options.length > 15, [options, canSearch]);

	const selectedValue = options.find(
		(v) =>
			v.Id === value?.Id ||
			v.Id === value ||
			(v.Code && ((value?.Code && v.Code === value?.Code) || value === v.Code)),
	);

	const isLoading = loading || insightQueryState.isLoading;

	useEffect(() => {
		if (!isLoading && autofillIfNull && selectedValue?.Id == null) {
			onChange(options[0]);
		}
	}, [options, autofillIfNull, isLoading]);

	const RadioGroupField = useCallback(() => {
		const items = options.map((o) => ({
			...o,
			value: o.Id,
			label: o.Name ?? o.Description,
		}));
		const val = items?.find((o) => o.value === selectedValue?.Id)?.value;

		return (
			<FolioRadioGroup
				value={val != null ? val : null}
				onChange={(e) => {
					const newValue = Number(e.target.value);
					const option = options.find((o) => o.Id === newValue);
					onChange(option);
					setTimeout(() => onBlur(e), 100);
				}}
				disabled={disabled}
				items={items}
			/>
		);
	}, [options, selectedValue, onChange, onBlur, disabled]);

	if (variant === 'radio' && options.length <= 5) {
		return <RadioGroupField />;
	}

	return (
		<Autocomplete
			fullWidth
			disabled={disabled || isLoading || options.length === 0}
			options={options}
			loading={isLoading}
			loadingText={<Skeleton variant='text' width='50%' />}
			value={selectedValue ?? null}
			isOptionEqualToValue={(option, val) =>
				option.Id === val?.Id || option.Name === val?.Name
			}
			onBlur={onBlur}
			getOptionLabel={(option) => option?.Name ?? ''}
			getOptionDisabled={(option) => isOptionDisabled(option)}
			filterOptions={(options, params) => {
				const search = params.inputValue;
				if (options.length > 0 && search && allowSearch) {
					return searchFuse.search(search).flatMap((v) => v?.item);
				}
				return options;
			}}
			disableClearable={!clearable}
			onChange={(e, option) => {
				onChange(option);
				onBlur(e);
			}}
			renderOption={(props, option) => (
				<li {...props} key={`${endpoint}-${option.Name}`}>
					{option.Name}
				</li>
			)}
			renderInput={(params) => {
				return (
					<TextField
						{...params}
						{...{ error }}
						variant={minimal ? 'standard' : 'outlined'}
						{...(!allowSearch && { onKeyDown: (e) => e.preventDefault() })}
						sx={{
							...(minimal && minimalInputTheme),
							'& .MuiInput-input': { color: theme.palette.primary.main },
							...(!allowSearch && { cursor: 'pointer' }),
						}}
						InputProps={{
							...params.InputProps,
							onBlur,
							...(!allowSearch && {
								readOnly: true,
								style: { caretColor: 'transparent', cursor: 'pointer' },
							}),
							...(isLoading && {
								endAdornment: (
									<InputAdornment position='end'>
										<CircularProgress size={20} />
									</InputAdornment>
								),
							}),
						}}
					/>
				);
			}}
			{...(props ?? {})}
			{...((props.id || props.name) && { id: props.id ?? props.name })}
			sx={{
				...(!allowSearch && {
					cursor: 'pointer',
					'& .MuiInputBase-root': { cursor: 'pointer' },
					'& input': { cursor: 'pointer' },
				}),

				...(minimal && {
					...minimalInputTheme,
					'& .MuiInputBase-root': { backgroundColor: 'transparent' },
				}),
				...(props?.sx ?? {}),
			}}
		/>
	);
};

export default InsightSelect;
